import { motion, AnimatePresence } from "framer-motion";
import { X, PartyPopper, Truck, Download, MessageCircle, Navigation, Share2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// POG detail panel — tap a POG tab / open a shared POG link (extracted from GpsOverlay, Map Cleanup Stage 4).
export const PogDetailPanel = ({ pogDetail, onClose, pogId, appInstalled, promptInstall, driver, loginGoogle, pogTrack, onCompose, routeTo, sharePog, copied }) => (
  <AnimatePresence>
    {pogDetail && (
      <motion.div
        className="absolute inset-0 z-[2050] flex items-center justify-center p-4 bg-black/55"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          data-testid="pog-detail-panel"
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong rounded-3xl w-full max-w-md overflow-hidden relative"
        >
          <button data-testid="pog-detail-close" onClick={onClose} className="absolute top-3 right-3 z-10 w-10 h-10 rounded-xl bg-slate-900/50 hover:bg-slate-900/80 flex items-center justify-center"><X className="w-5 h-5 text-white" /></button>
          {pogDetail.ad_image_url && (
            <img src={`${BACKEND_URL}${pogDetail.ad_image_url}`} alt="" className="w-full h-44 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <div className="p-5">
            {pogId && !appInstalled() && (
              <div data-testid="pogd-recipient-banner" className="-mt-1 mb-3 rounded-2xl bg-gradient-to-r from-fuchsia-500/25 to-violet-500/25 border border-fuchsia-400/30 p-3 text-center">
                <p className="font-heading font-extrabold text-fuchsia-100 text-base">You've been POG'd! 🎉</p>
                <p className="text-[11px] text-slate-300 mt-0.5">Install AIR.POG to receive POGs & catch deals as you go.</p>
              </div>
            )}
            <span className="inline-flex items-center gap-1 bg-fuchsia-500 text-white text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-md mb-2">
              <PartyPopper className="w-3 h-3" /> {pogDetail.sale_type || "POG"}{pogDetail.visibility === "private" ? " · Private" : ""}
            </span>
            <h2 className="font-heading text-xl font-bold leading-tight mb-1">{pogDetail.name}</h2>
            {pogDetail.sale_when && <p className="text-sm text-sky-300 font-semibold mb-1">{pogDetail.sale_when}</p>}
            {pogDetail.address && <p className="text-xs text-slate-400 mb-2">{pogDetail.address}</p>}
            {pogDetail.location_mode === "delivery" && (
              <div data-testid="pogd-delivery-badge" className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-2.5 mb-3">
                <p className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
                  <Truck className="w-4 h-4" /> Delivery only · address hidden
                  {pogDetail.delivery_radius_mi ? <span className="text-amber-200/90 font-semibold">· delivers within {pogDetail.delivery_radius_mi} mi</span> : null}
                </p>
                {pogDetail.delivery_note && <p className="text-[11px] text-slate-300 mt-1">{pogDetail.delivery_note}</p>}
              </div>
            )}
            {pogDetail.sale_detail && <p className="text-sm text-slate-300 mb-3 leading-relaxed">{pogDetail.sale_detail}</p>}
            {pogDetail.sale_tags && pogDetail.sale_tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {pogDetail.sale_tags.map((t, i) => <span key={`${t}-${i}`} className="text-[11px] bg-slate-800/70 text-slate-300 rounded-full px-2.5 py-1">{t}</span>)}
              </div>
            )}
            {pogId && !appInstalled() && (
              <button
                data-testid="pogd-install-btn"
                onClick={promptInstall}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-heading font-bold flex items-center justify-center gap-2 hover:opacity-90 mb-2"
              >
                <Download className="w-5 h-5" /> Install AIR.POG
              </button>
            )}
            <div className="flex gap-2 mt-2">
              {pogDetail.contact ? (
                <button
                  data-testid="pog-request-delivery-btn"
                  onClick={() => {
                    if (!driver) { loginGoogle?.(); return; }
                    pogTrack(pogDetail.id, "contact");
                    onCompose({ sale_id: pogDetail.id, title: pogDetail.name || pogDetail.title || "this listing", contact: pogDetail.contact });
                  }}
                  className="flex-1 h-12 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 font-heading font-bold flex items-center justify-center gap-2 hover:opacity-90"
                >
                  <MessageCircle className="w-5 h-5" /> {pogDetail.location_mode === "delivery" ? "Request delivery" : "Message seller"}
                </button>
              ) : (
                <button
                  data-testid="pog-route-btn"
                  onClick={() => { const p = pogDetail; onClose(); if (p?.lat) { pogTrack(p.id, "route"); routeTo(p); } }}
                  className="flex-1 h-12 rounded-xl bg-gradient-to-r from-sky-500 to-violet-500 text-white font-heading font-bold flex items-center justify-center gap-2 hover:opacity-90"
                >
                  <Navigation className="w-5 h-5" /> {pogDetail.location_mode === "delivery" ? "Route to area" : "Route here"}
                </button>
              )}
              <button
                data-testid="pog-detail-share-btn"
                onClick={() => sharePog(pogDetail.id)}
                className="h-12 px-4 rounded-xl bg-slate-800/70 hover:bg-slate-700/70 text-slate-200 flex items-center gap-1.5 font-bold text-sm"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
